import React, { useState } from "react";
import { connect } from "react-redux";
import { RootState } from "../../../store"; // поправь путь
import { ClientWithDeal, DealStatusCatalog } from "../types";
import { STATUS_CATALOG_DEFAULT } from "../constants";
import { buildLink, formatMoney } from "../utils";
import { FinalSelectionCard } from "../FinalSelectionCard";
import { ClientDealsList } from "../ClientDealsList"; // <- компонент со сделками
import { Pencil, Trash2 } from "lucide-react";
import { formatPhoneForDisplay } from "../../../utils";

type OwnProps = {
    rows: ClientWithDeal[];
    catalog?: DealStatusCatalog;
    onOpenClient?: (id: string) => void;
    onEditClient: (client: ClientWithDeal) => void;
    onDeleteClient: (client: ClientWithDeal) => void;
    onDealsChanged?: () => void | Promise<void>;
};

type StateProps = {};
type DispatchProps = {};
type Props = OwnProps & StateProps & DispatchProps;

const _ClientList: React.FC<Props> = ({
                                          rows,
                                          catalog = STATUS_CATALOG_DEFAULT,
                                          onOpenClient,
                                          onEditClient,
                                          onDeleteClient,
                                          onDealsChanged,
                                      }) => {
    // теперь можно раскрывать несколько клиентов
    const [expandedIds, setExpandedIds] = useState<Array<string | number>>([]);

    if (!rows?.length) {
        return <div className="text-sm text-gray-500 py-6">Клиенты не найдены</div>;
    }

    const handleToggleDeals = (id: number | string) => {
        setExpandedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    return (
        <div className="divide-y">
            {rows.map((r) => {
                const hasFinal =
                    r.deal?.statusId === "closed_success" && !!r.deal?.finalSelection;
                const isExpanded = expandedIds.includes(r.id);
                const phone = formatPhoneForDisplay(r.phone);

                return (
                    <div key={r.id} className="py-3 px-2 rounded hover:bg-gray-50">
                        <div className="flex items-start justify-between gap-3">
                            {/* Левая колонка: инфо */}
                            <div
                                className={onOpenClient ? "min-w-0 flex-1 cursor-pointer" : "min-w-0 flex-1"}
                                onClick={() => onOpenClient?.(r.id)}
                                role={onOpenClient ? "button" : undefined}
                                tabIndex={onOpenClient ? 0 : undefined}
                                onKeyDown={(event) => {
                                    if (onOpenClient && (event.key === "Enter" || event.key === " ")) {
                                        event.preventDefault();
                                        onOpenClient(r.id);
                                    }
                                }}
                            >
                                <div className="font-medium truncate">
                                    {r.fullName || "Без имени"}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                    {phone || r.email || r.telegram || "—"}
                                </div>

                                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500">
                                    <span>
                                        В работе:{" "}
                                        <strong className="font-medium text-gray-700">
                                            {r.inProgressDealsCount ?? 0}
                                        </strong>
                                    </span>
                                    <span>
                                        Завершено:{" "}
                                        <strong className="font-medium text-gray-700">
                                            {r.completedDealsCount ?? 0}
                                        </strong>
                                    </span>
                                    <span>
                                        Объём:{" "}
                                        <strong className="font-medium text-gray-700">
                                            {formatMoney(r.completedDealsPropertyPriceSum ?? 0)}
                                        </strong>
                                    </span>
                                </div>

                                {r.queryText && (
                                    <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                                        Запрос: {r.queryText}
                                    </div>
                                )}

                                {r.lastComment?.text && (
                                    <div className="text-xs text-gray-400 mt-1 line-clamp-1">
                                        Комментарий: {r.lastComment.text}
                                    </div>
                                )}

                                {/* Прикрепления */}
                                {r.attachments?.length ? (
                                    <div className="flex flex-wrap items-center gap-2 mt-2">
                                        {r.attachments.map((att) => (
                                            <a
                                                key={att.id}
                                                href={buildLink(att.kind, att.externalId)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-xs px-2 py-1 rounded border hover:bg-gray-100"
                                                onClick={(e) => e.stopPropagation()}
                                                title={
                                                    att.title ||
                                                    (att.kind === "calculation"
                                                        ? "Расчёт"
                                                        : "Подборка")
                                                }
                                            >
                                                {att.kind === "calculation" ? "Расчёт" : "Подборка"}
                                            </a>
                                        ))}
                                    </div>
                                ) : null}
                            </div>

                            <div className="flex shrink-0 items-center gap-1">
                                <button
                                    type="button"
                                    className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#c06198]/30"
                                    onClick={() => onEditClient(r)}
                                    aria-label={`Редактировать клиента ${r.fullName || ""}`}
                                    title="Редактировать"
                                >
                                    <Pencil size={16} aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    className="rounded-md p-2 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-200"
                                    onClick={() => onDeleteClient(r)}
                                    aria-label={`Удалить клиента ${r.fullName || ""}`}
                                    title="Удалить"
                                >
                                    <Trash2 size={16} aria-hidden="true" />
                                </button>
                            </div>
                        </div>

                        {/* Итоговый объект — по универсальному снапшоту */}
                        {hasFinal && r.deal?.finalSelection ? (
                            <FinalSelectionCard finalSelection={r.deal.finalSelection} />
                        ) : null}

                        {/* Кнопка для открытия/закрытия списка сделок */}
                        <div className="mt-2">
                            <button
                                type="button"
                                onClick={() => handleToggleDeals(r.id)}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                                {isExpanded ? "Скрыть сделки" : "Показать сделки"}
                            </button>
                        </div>

                        {/* Вложенный список сделок */}
                        <ClientDealsList
                            clientId={r.id}
                            isOpen={isExpanded}
                            onDealsChanged={onDealsChanged}
                        />
                    </div>
                );
            })}
        </div>
    );
};

const mapStateToProps = (_state: RootState): StateProps => ({});
const mapDispatchToProps: DispatchProps = {};

export const ClientList = connect(
    mapStateToProps,
    mapDispatchToProps,
)(_ClientList);
